import { ContactModal } from '@/features/contact-modal/ui/contact-modal';
import { OrganizationFormModal } from '@/features/organization-form-modal';

import { useContactSelector } from '../model';
import { OrganizationSelect } from './organization-select';
import { ContactSelect } from './contact-select';
import { ContactDetails } from './contact-details';

interface ContactSelectorProps {
  label: string;
  organizationId?: string;
  organizationName?: string;
  contactId?: string;
  contactName?: string;
  onOrganizationChange: (id: string | undefined) => void;
  onContactChange: (id: string | undefined) => void;
  className?: string;
  disabled?: boolean;
  required?: boolean;
  organizationError?: string;
  contactError?: string;
}

export const ContactSelector = ({
  label,
  organizationId,
  organizationName,
  contactId,
  contactName,
  onOrganizationChange,
  onContactChange,
  className,
  disabled = false,
  required = false,
  organizationError,
  contactError
}: ContactSelectorProps) => {
  const {
    // State
    isContactModalOpen,
    showContactDetails,
    isLoadingOrgs,
    isLoadingContacts,
    orgOptions,
    contactOptions,
    selectedContact,
    contactModalMode,
    isOrgModalOpen,
    orgModalMode,
    // Handlers
    handleContactAdd,
    handleContactOpen,
    handleContactModalClose,
    handleContactModalSuccess,
    toggleContactDetails,
    handleOrgOpen,
    handleOrgModalClose,
    handleOrganizationChange
  } = useContactSelector({
    organizationId,
    organizationName,
    contactId,
    contactName,
    onOrganizationChange,
    onContactChange,
    disabled
  });

  return (
    <div className={className}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 items-start">
        <OrganizationSelect
          label={label}
          organizationId={organizationId}
          options={orgOptions}
          isLoading={isLoadingOrgs}
          disabled={disabled}
          required={required}
          error={organizationError}
          onChange={handleOrganizationChange}
          handleOrgOpen={handleOrgOpen}
        />

        <ContactSelect
          label={label}
          contactId={contactId}
          options={contactOptions}
          isLoading={isLoadingContacts}
          disabled={disabled}
          required={required}
          error={contactError}
          showDetails={showContactDetails}
          onChange={onContactChange}
          onDetailsClick={toggleContactDetails}
          onEditClick={handleContactOpen}
          onAddClick={handleContactAdd}
        />
      </div>

      {selectedContact && showContactDetails && <ContactDetails contact={selectedContact} />}

      <ContactModal
        isOpen={isContactModalOpen}
        onClose={handleContactModalClose}
        onSuccess={handleContactModalSuccess}
        initialData={
          contactModalMode !== 'create'
            ? selectedContact
            : { orgId: organizationId ? Number(organizationId) : undefined }
        }
        mode={contactModalMode}
      />

      <OrganizationFormModal
        isOpen={isOrgModalOpen}
        onClose={handleOrgModalClose}
        organizationId={Number(organizationId)}
        mode={orgModalMode}
        onSuccess={handleOrgModalClose}
      />
    </div>
  );
};
